import { forwardRef, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Home, Phone, User, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMissedCallsCount } from "@/hooks/useMissedCallsCount";
import { hapticSelection } from "@/lib/native-haptics";
import { useIsCallActive } from "@/lib/active-call-store";
import { prefetchRoute } from "@/lib/route-prefetch";
import { toast } from "sonner";
import { useIsNative } from "@/hooks/useIsNative";

/**
 * Apple Wallet IA - 4 canonical anchors: Home · Call · Wallet · Me.
 * - Contacts & History live inside Call (Recents | Keypad | Contacts tabs).
 * - Wallet is a first-class tab so users never hunt for credit / top-up.
 * - Rates are surfaced inside Call (per-country) and inside Wallet (full table).
 */
export const ConnectPortalBottomNav = forwardRef<HTMLElement, Record<string, never>>(function ConnectPortalBottomNav(_, ref) {
  const location = useLocation();
  const { t } = useTranslation("common");
  const { count: missedCallsCount } = useMissedCallsCount();
  const callActive = useIsCallActive();
  const isNative = useIsNative();

  const callHref = missedCallsCount > 0 ? "/connect-portal/dial?tab=recents" : "/connect-portal/dial";

  const navItems = useMemo(() => [
    { label: t("home"), icon: Home, href: "/connect-portal/dashboard", badge: null },
    ...(isNative ? [{ label: t("call"), icon: Phone, href: callHref, badge: null }] : []),
    { label: t("wallet", { defaultValue: "Wallet" }), icon: Wallet, href: "/connect-portal/topup", badge: null },
    { label: t("account"), icon: User, href: "/connect-portal/me", badge: null },
  ], [t, callHref, isNative]);

  const isActive = (href: string) => {
    const hrefPath = href.split('?')[0];
    if (hrefPath === "/connect-portal/dashboard") {
      return location.pathname === "/connect-portal" || location.pathname === "/connect-portal/dashboard";
    }
    if (hrefPath === "/connect-portal/me") {
      return location.pathname === "/connect-portal/me" || location.pathname.startsWith("/connect-portal/me/");
    }
    if (hrefPath === "/connect-portal/topup") {
      // Wallet tab is "active" on top-up surface AND the rates browser
      return location.pathname.startsWith("/connect-portal/topup") || location.pathname.startsWith("/connect-portal/rates");
    }
    return location.pathname.startsWith(hrefPath);
  };

  // STABILITY FIX: previously paddingTop ALSO used env(safe-area-inset-bottom),
  // which changes when overlaysWebView toggles (e.g. entering/leaving the call
  // screen). That caused the visible "nav jumps position seconds later" bug.
  // Top padding is now a fixed 6px so the icon row never shifts; only the
  // bottom uses the safe-area inset (home indicator on notched iPhones).
  return (
    <nav
      ref={ref}
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border shadow-[0_-6px_20px_-8px_hsl(var(--foreground)/0.22)]"
      style={{
        backgroundColor: "hsl(var(--background))",
        paddingTop: "6px",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <div className="mx-auto flex h-[52px] max-w-md items-center justify-around px-1">

        {navItems.map((item) => {
          const active = isActive(item.href);
          // While a PSTN call is live, only the Call tab is reachable - everything
          // else is greyed out so the user can never accidentally leave the call
          // surface mid-conversation (regression: backgrounding wiped call context).
          const isCallTab = item.href.startsWith("/connect-portal/dial");
          const locked = callActive && !isCallTab;
          return (
            <NavLink
              key={item.href}
              to={locked ? "/connect-portal/dial" : item.href}
              aria-label={item.label}
              aria-disabled={locked || undefined}
              onPointerDown={() => {
                // Warm the destination chunk during the ~80ms finger-down
                // window so the route commits with zero parse latency.
                if (!locked) prefetchRoute(item.href);
              }}
              onClick={(e) => {
                if (locked) {
                  e.preventDefault();
                  toast.info(t("callInProgress", { defaultValue: "Call in progress - finish your call to use this." }));
                  return;
                }
                if (!active) void hapticSelection();
              }}
              className={cn(
                "relative flex h-full flex-1 flex-col items-center justify-center gap-0.5 px-2 transition-colors duration-200 touchable no-context-menu",
                active ? "text-primary" : "text-foreground/80",
                item.badge === 'Soon' && !active && "opacity-60",
                locked && "opacity-40 pointer-events-auto"
              )}

            >
              <div className="relative flex h-7 w-12 items-center justify-center">
                {/* Pill sits behind the icon so layout never shifts between tabs */}
                <span
                  aria-hidden
                  className={cn(
                    "absolute inset-0 rounded-full transition-all duration-200",
                    active ? "bg-primary/10 scale-100 opacity-100" : "scale-75 opacity-0"
                  )}
                />
                <item.icon
                  className="relative h-[22px] w-[22px] transition-all duration-200"
                  strokeWidth={active ? 2.2 : 1.8}
                />
                {item.href.startsWith("/connect-portal/dial") && missedCallsCount > 0 && (
                  <span className="absolute -top-0.5 right-1 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1">
                    {missedCallsCount > 99 ? '99+' : missedCallsCount}
                  </span>
                )}
                {item.badge === 'Soon' && (
                  <span className="absolute -top-1 -right-1 min-w-[28px] h-[14px] rounded-full bg-primary/20 text-primary text-[8px] font-bold flex items-center justify-center px-1">
                    Soon
                  </span>
                )}
              </div>
              <span className={cn(
                "text-[11px] leading-none",
                active ? "font-semibold" : "font-medium"
              )}>{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
});

ConnectPortalBottomNav.displayName = "ConnectPortalBottomNav";
